/**
 * Photo Upload Core - Shared upload utilities
 * This module contains the shared logic for photo uploads used by both:
 * - photo-upload.js (jQuery-based, for private messages)
 * - photo-uploader.js (ES modules, for Alpine components)
 */

import { getApiToken } from './tokenUtils.js';
import { t } from './i18n.js';
import { apiErrorMessage } from './api-error-message.js';
import { trackEvent } from './ga-event.js';

export const PHOTO_UPLOAD_ENDPOINT = '/v1/photo';

/**
 * Configuration defaults
 */
export const PhotoUploadConfig = {
    MAX_FILE_SIZE: 10 * 1024 * 1024, // 10MB
    ALLOWED_TYPES: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
    ALLOWED_EXTENSIONS: ['.jpg', '.jpeg', '.png', '.gif', '.webp']
};

/**
 * Error messages for photo uploads
 * Returns translated strings via getter functions
 */
export const PhotoUploadErrors = {
    get NO_FILE() { return t('common.photo_no_file'); },
    get INVALID_TYPE() { return t('common.photo_invalid_type'); },
    get FILE_TOO_LARGE() { return t('common.photo_file_too_large'); },
    get AUTH_REQUIRED() { return t('common.photo_auth_required'); },
    get UPLOAD_FAILED() { return t('common.photo_upload_failed'); },
    get NETWORK_ERROR() { return t('common.photo_network_error'); },
    get UPLOAD_CANCELLED() { return t('common.photo_upload_cancelled'); },
    get INVALID_RESPONSE() { return t('common.photo_invalid_response'); },
    get NO_PHOTO_ID() { return t('common.photo_no_id_response'); }
};

/**
 * Validate a file before upload
 * @param {File} file - File to validate
 * @returns {{ valid: boolean, error?: string }}
 */
export function validateFile(file) {
    if (!file) {
        return { valid: false, error: PhotoUploadErrors.NO_FILE };
    }

    // Check MIME type
    if (!file.type.startsWith('image/')) {
        return { valid: false, error: PhotoUploadErrors.INVALID_TYPE };
    }

    if (!PhotoUploadConfig.ALLOWED_TYPES.includes(file.type)) {
        // Fallback: check extension
        const extension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
        if (!PhotoUploadConfig.ALLOWED_EXTENSIONS.includes(extension)) {
            return { valid: false, error: PhotoUploadErrors.INVALID_TYPE };
        }
    }

    // Check file size
    if (file.size > PhotoUploadConfig.MAX_FILE_SIZE) {
        const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
        return { valid: false, error: t('common.photo_file_too_large_detail').replace('{size}', sizeMB) };
    }

    return { valid: true };
}

/**
 * Parse JSON response preserving large integer IDs as strings
 * @param {string} responseText - Raw JSON response
 * @returns {Object} Parsed response with IDs as strings
 */
export function parsePhotoResponse(responseText) {
    // Quote ID tokens before JSON.parse rounds them. Consume other strings intact.
    const exactIds = responseText.replace(
        /("(?:\\.|[^"\\])*")(\s*:\s*)(-?(?:0|[1-9]\d*))(?=\s*[,}])|"(?:\\.|[^"\\])*"/g,
        (token, key, separator, integer) => {
            if (!key || !['id', 'photo_id'].includes(JSON.parse(key))) return token;
            return `${key}${separator}"${integer}"`;
        }
    );
    return JSON.parse(exactIds, (key, value) => {
        if ((key === 'id' || key === 'photo_id') && typeof value === 'number') {
            return String(value);
        }
        return value;
    });
}

/**
 * Parse server error status into user-friendly message
 * @param {number} status - HTTP status code
 * @param {string} [responseText] - Optional response body
 * @returns {string} User-friendly error message
 */
export function parseServerError(status, responseText) {
    // Prefer the message the API sent, which is localized for the requesting user
    const message = apiErrorMessage(responseText);
    if (message) {
        return message;
    }

    switch (status) {
        case 401:
        case 403:
            return t('common.photo_not_authorized');
        case 413:
            return t('common.photo_choose_smaller');
        case 415:
            return t('common.photo_choose_valid_type');
        case 500:
        case 502:
        case 503:
            return t('common.photo_server_error');
        default:
            return `${PhotoUploadErrors.UPLOAD_FAILED}: ${status}`;
    }
}

/**
 * An upload failure: the translated message, plus what went wrong for a caller that maps it.
 * @param {string} message
 * @param {'token'|'network'|'aborted'|'http'|'response'} reason
 * @param {XMLHttpRequest} [xhr]
 * @returns {Error & { reason: string, status: number|null, responseText: string|null }}
 */
function uploadFailure(message, reason, xhr) {
    return Object.assign(new Error(message), {
        reason,
        status: xhr ? xhr.status : null,
        responseText: xhr ? xhr.responseText : null,
    });
}

/**
 * Get authentication token for API requests
 * Fetches token from server if not present in localStorage
 * @returns {Promise<string|null>} Auth token
 */
async function getAuthToken() {
    return await getApiToken();
}

/**
 * Upload a photo with XHR (for progress tracking)
 * @param {Object} options - Upload options
 * @param {File} options.file - File to upload
 * @param {string} [options.token] - Auth token (will fetch if not provided)
 * @param {string} [options.albumId] - Optional album ID
 * @param {AbortController} [options.controller] - Optional abort controller
 * @param {function(number): void} [options.onProgress] - Progress callback (0-100)
 * @returns {Promise<{ id: string, [key: string]: any }>} Upload result with photo ID
 */
export async function uploadPhoto({ file, token, albumId, controller, onProgress }) {
    // Get token if not provided
    const authToken = token || await getAuthToken();
    if (!authToken) {
        throw uploadFailure(PhotoUploadErrors.AUTH_REQUIRED, 'token');
    }

    const formData = new FormData();
    formData.append('file', file);
    if (albumId) {
        formData.append('aid', albumId);
    }

    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        // Handle abort signal if provided
        if (controller) {
            controller.signal.addEventListener('abort', () => {
                xhr.abort();
            });
        }

        // Track upload progress
        xhr.upload.addEventListener('progress', (e) => {
            if (e.lengthComputable && onProgress) {
                const percent = Math.round((e.loaded / e.total) * 100);
                onProgress(percent);
            }
        });

        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                try {
                    const data = parsePhotoResponse(xhr.responseText);
                    if (!data || !data.id) {
                        // A body with no photo id can still name the reason the
                        // upload was rejected; showing it beats a message about
                        // the response the user cannot act on.
                        reject(uploadFailure(apiErrorMessage(xhr.responseText) || PhotoUploadErrors.NO_PHOTO_ID, 'response', xhr));
                        return;
                    }
                    trackEvent('photo_uploaded');
                    resolve(data);
                } catch {
                    reject(uploadFailure(PhotoUploadErrors.INVALID_RESPONSE, 'response', xhr));
                }
            } else {
                reject(uploadFailure(parseServerError(xhr.status, xhr.responseText), 'http', xhr));
            }
        };

        xhr.onerror = () => reject(uploadFailure(PhotoUploadErrors.NETWORK_ERROR, 'network'));
        xhr.onabort = () => reject(uploadFailure(PhotoUploadErrors.UPLOAD_CANCELLED, 'aborted'));

        xhr.open('POST', PHOTO_UPLOAD_ENDPOINT, true);
        xhr.setRequestHeader('X-AUTH-TOKEN', authToken);
        xhr.send(formData);
    });
}

/**
 * Delete a photo
 * @param {string} photoId - Photo ID to delete
 * @param {string} [token] - Auth token (will fetch if not provided)
 * @returns {Promise<void>}
 */
export async function deletePhoto(photoId, token) {
    const authToken = token || await getAuthToken();
    if (!authToken) {
        throw new Error(PhotoUploadErrors.AUTH_REQUIRED);
    }

    const response = await fetch(`${PHOTO_UPLOAD_ENDPOINT}/${photoId}`, {
        method: 'DELETE',
        headers: {
            'X-AUTH-TOKEN': authToken
        },
        credentials: 'include'
    });

    if (!response.ok && response.status !== 204) {
        throw new Error(parseServerError(response.status));
    }
}
